import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { useUser } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Upload, Trash2, FileText, Brain, Pencil } from "lucide-react";
import { toast } from "sonner";
import { formatBytes } from "@/lib/utils";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { EmptyState } from "@/components/ui/empty-state";
import { UpgradePromptDialog } from "@/components/billing/UpgradePromptDialog";

export default function KnowledgeBasePage() {
  const { user } = useUser();
  const organization = useQuery(api.organizations.getByUser, user ? {} : "skip");
  // Use the _id as workspaceId string - works for both organizations and personal workspaces
  const workspaceId = organization?._id?.toString();

  const documents = useQuery(
    api.knowledgeBase.listDocuments,
    workspaceId ? { workspaceId } : "skip"
  );
  const generateUploadUrl = useMutation(api.knowledgeBase.generateUploadUrl);
  const createDocument = useMutation(api.knowledgeBase.createDocument);
  const deleteDocument = useMutation(api.knowledgeBase.deleteDocument);
  const updateDocument = useMutation(api.knowledgeBase.updateDocument);

  const subscription = useQuery(api.subscriptions.getUserSubscription, user ? {} : "skip");
  const hasPurchasedCredits = Boolean((subscription as any)?.purchasedCredits && (subscription as any).purchasedCredits > 0);
  const hasActivePaidPlan = Boolean(subscription && subscription.status === "active" && (subscription.amount || 0) > 0);
  const isCreditBlocked = subscription !== undefined && !hasPurchasedCredits && !hasActivePaidPlan;
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Define document type based on query result
  type KnowledgeBaseDoc = NonNullable<typeof documents>[number];

  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [accessLevel, setAccessLevel] = useState<"management" | "company" | "public">("company");
  const [isUploading, setIsUploading] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Edit document state
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<KnowledgeBaseDoc | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editAccessLevel, setEditAccessLevel] = useState<"management" | "company" | "public">(
    "company"
  );
  const [isUpdating, setIsUpdating] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      if (!title) {
        setTitle(e.target.files[0].name);
      }
    }
  };

  const handleUpload = async () => {
    if (!file || !workspaceId) return;

    setIsUploading(true);
    try {
      // 1. Get upload URL
      const postUrl = await generateUploadUrl();

      // 2. Upload file
      const result = await fetch(postUrl, {
        method: "POST",
        headers: { "Content-Type": file.type },
        body: file,
      });

      if (!result.ok) throw new Error("Upload failed");
      const { storageId } = await result.json();

      // 3. Create document record
      await createDocument({
        title,
        description,
        storageId,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        accessLevel,
        workspaceId,
      });

      toast.success("Document uploaded successfully");
      setIsUploadOpen(false);
      setFile(null);
      setTitle("");
      setDescription("");
      setAccessLevel("company");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload document");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!documentToDelete) return;

    setIsDeleting(true);
    try {
      await deleteDocument({ documentId: documentToDelete as any });
      setDocumentToDelete(null);
      toast.success("Document deleted");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete document");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditClick = (doc: KnowledgeBaseDoc) => {
    setEditingDocument(doc);
    setEditTitle(doc.title);
    setEditDescription(doc.description || "");
    setEditAccessLevel(doc.accessLevel);
    setIsEditOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingDocument) return;

    setIsUpdating(true);
    try {
      await updateDocument({
        documentId: editingDocument._id,
        title: editTitle,
        description: editDescription,
        accessLevel: editAccessLevel,
      });
      toast.success("Document updated successfully");
      setIsEditOpen(false);
      setEditingDocument(null);
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Failed to update document");
    } finally {
      setIsUpdating(false);
    }
  };

  if (!workspaceId) return <div className="p-8">Please select an organization.</div>;

  return (
    <>
      <UpgradePromptDialog
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title="Upgrade to Starter"
        description="Upload documents to train your AI Business Assistant for highly personalized responses."
        feature="AI Knowledge Base Training"
        planName="Starter"
        planFeatures={["Unlimited Bids & Proposals", "Connect apps like Gmail & Notion", "Advanced Analytics"]}
      />
    <DashboardLayout activeTab="knowledge-base">
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="flex items-center gap-2 text-3xl font-bold">
              <Brain className="h-8 w-8 text-primary" />
              AI Knowledge Base
            </h1>
            <p className="mt-2 text-muted-foreground">
              Manage documents for the AI Agents.
            </p>
          </div>
          <Button onClick={() => {
            if (isCreditBlocked) {
              setShowUpgradeModal(true);
            } else {
              setIsUploadOpen(true);
            }
          }}>
            <Upload className="mr-2 h-4 w-4" />
            Upload Document
          </Button>
          <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Upload AI Knowledge Base Document</DialogTitle>
                <DialogDescription>
                  Upload a PDF or text file to train the AI assistant.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="file">File</Label>
                  <Input
                    id="file"
                    type="file"
                    onChange={handleFileChange}
                    accept=".pdf,.txt,.md,.docx"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Document Title"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Brief description"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="access">Access Level</Label>
                  <Select value={accessLevel} onValueChange={(v: any) => setAccessLevel(v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="management">Management Only</SelectItem>
                      <SelectItem value="company">All Employees</SelectItem>
                      <SelectItem value="public">Public</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsUploadOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleUpload} disabled={!file || isUploading}>
                  {isUploading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Upload
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Documents</CardTitle>
          </CardHeader>
          <CardContent>
            {!documents ? (
              <div className="flex justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : documents.length === 0 ? (
              <div className="py-12">
                <EmptyState
                  icon={FileText}
                  title="No documents yet"
                  description="Upload a document to train the AI assistant."
                  actionLabel="Upload Document"
                  actionIcon={Upload}
                  onAction={() => {
                    if (isCreditBlocked) {
                      setShowUpgradeModal(true);
                    } else {
                      setIsUploadOpen(true);
                    }
                  }}
                />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Access</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.map((doc: KnowledgeBaseDoc) => (
                    <TableRow key={doc._id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <div>{doc.title}</div>
                            <div className="text-xs text-muted-foreground">{doc.fileName}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            doc.accessLevel === "management"
                              ? "destructive"
                              : doc.accessLevel === "company"
                                ? "default"
                                : "secondary"
                          }
                        >
                          {doc.accessLevel}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatBytes(doc.fileSize)}</TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                            doc.status === "active"
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => handleEditClick(doc)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDocumentToDelete(doc._id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog
        open={!!documentToDelete}
        onOpenChange={(open) => !open && setDocumentToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the document and remove it
              from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Document Dialog */}
      <Dialog
        open={isEditOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsEditOpen(false);
            setEditingDocument(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Document</DialogTitle>
            <DialogDescription>Update the document information.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title">Title</Label>
              <Input
                id="edit-title"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Document Title"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Input
                id="edit-description"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Brief description"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-access">Access Level</Label>
              <Select
                value={editAccessLevel}
                onValueChange={(v: "management" | "company" | "public") => setEditAccessLevel(v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="management">Management Only</SelectItem>
                  <SelectItem value="company">All Employees</SelectItem>
                  <SelectItem value="public">Public</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={!editTitle.trim() || isUpdating}>
              {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
    </>
  );
}
